import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { api } from "@/services/api";
import { i18nToast } from "@/utils/i18nToast";
import {
  AlertCircle,
  Download,
  Eye,
  FileImage,
  FileText,
  Grid,
  List,
  Lock,
  MoreVertical,
  Search,
  Share2,
  Shield,
  Tag,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import type { StoredDocument } from "@/types/document";

interface SecureDocumentListProps {
  documents?: StoredDocument[];
  onDocumentSelect?: (document: StoredDocument) => void;
  onDocumentDelete?: (documentId: string) => void;
  viewMode?: "grid" | "list";
  showUpload?: boolean;
  allowViewModeToggle?: boolean;
}

const DOCUMENT_CATEGORIES = {
  HEALTH_RECORD: {
    label: "Health Record",
    icon: "🏥",
    color: "bg-red-100 text-red-800",
  },
  VACCINATION: {
    label: "Vaccinations",
    icon: "💉",
    color: "bg-green-100 text-green-800",
  },
  PRESCRIPTION: {
    label: "Prescriptions",
    icon: "💊",
    color: "bg-blue-100 text-blue-800",
  },
  INSURANCE: {
    label: "Insurance",
    icon: "🛡️",
    color: "bg-purple-100 text-purple-800",
  },
  TRAVEL: {
    label: "Travel Documents",
    icon: "✈️",
    color: "bg-indigo-100 text-indigo-800",
  },
  OTHER: { label: "Other", icon: "📄", color: "bg-gray-100 text-gray-800" },
};

export const SecureDocumentList: React.FC<SecureDocumentListProps> = ({
  documents: initialDocuments = [],
  onDocumentSelect,
  onDocumentDelete,
  viewMode: initialViewMode = "grid",
  showUpload = true,
  allowViewModeToggle = true,
}) => {
  const fmt = useLocaleFormat();
  const [documents, setDocuments] =
    useState<StoredDocument[]>(initialDocuments);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">(initialViewMode);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [showPrivateOnly, setShowPrivateOnly] = useState(false);
  const [showDropdown, setShowDropdown] = useState<string | null>(null);
  const effectiveViewMode: "grid" | "list" = allowViewModeToggle
    ? viewMode
    : "list";

  const normalizeDocuments = (rawDocuments: any[]): StoredDocument[] => {
    return rawDocuments.map((doc) => ({
      id: String(doc.id || doc._id || ""),
      fileName: doc.fileName || doc.originalName || "",
      originalName: doc.originalName || doc.fileName || "",
      category: doc.category || doc.type || "OTHER",
      description: doc.description || "",
      isPrivate: doc.isPrivate ?? true,
      isEncrypted: doc.isEncrypted ?? false,
      fileSize: doc.fileSize ?? doc.size ?? 0,
      mimeType: doc.mimeType || "application/octet-stream",
      bucketName: doc.bucketName,
      objectName: doc.objectName || doc.storagePath,
      tags: Array.isArray(doc.tags) ? doc.tags : [],
      createdAt: doc.createdAt || doc.uploadedAt || new Date().toISOString(),
      updatedAt: doc.updatedAt || doc.uploadedAt || new Date().toISOString(),
      user: doc.user,
    }));
  };

  // Load documents on mount
  useEffect(() => {
    if (initialDocuments.length === 0) {
      loadDocuments();
    }
  }, [initialDocuments.length]);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get("/documents");
      const result = response.data;
      if (result.success) {
        setDocuments(normalizeDocuments(result.data?.documents || []));
      }
    } catch (error: any) {
      console.error("Error loading documents:", error);
      i18nToast.error("Error loading documents: " + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Filter documents based on search and filters
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      searchTerm === "" ||
      doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.tags.some((tag) =>
        tag.toLowerCase().includes(searchTerm.toLowerCase()),
      );

    const matchesCategory =
      selectedCategory === "" || doc.category === selectedCategory;
    const matchesPrivacy = !showPrivateOnly || doc.isPrivate;

    return matchesSearch && matchesCategory && matchesPrivacy;
  });

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatDate = (dateString: string) => fmt.dateTime(dateString, "medium");

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith("image/")) {
      return <FileImage className="w-5 h-5 text-gray-500" aria-hidden="true" />;
    }
    return <FileText className="w-5 h-5 text-gray-500" aria-hidden="true" />;
  };

  const handleDownload = async (doc: StoredDocument) => {
    try {
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = url;
      a.download = doc.originalName;
      window.document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(a);

      i18nToast.success("Document downloaded");
    } catch (error: any) {
      console.error("Download error:", error);
      i18nToast.error("Download failed: " + error.message);
    }
  };

  const handleDelete = async (document: StoredDocument) => {
    if (!confirm(`Delete document "${document.originalName}"?`)) {
      return;
    }

    try {
      await api.delete(`/documents/${document.id}`);

      setDocuments((prev) => prev.filter((d) => d.id !== document.id));
      onDocumentDelete?.(document.id);
      i18nToast.success("Document deleted");
    } catch (error: any) {
      console.error("Delete error:", error);
      i18nToast.error("Delete failed: " + error.message);
    }
  };

  const DocumentCard: React.FC<{ document: StoredDocument }> = ({
    document,
  }) => {
    const category =
      DOCUMENT_CATEGORIES[
        document.category as keyof typeof DOCUMENT_CATEGORIES
      ];

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                {getFileIcon(document.mimeType)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-medium text-gray-900 truncate">
                  {document.originalName}
                </h3>
                <p className="text-xs text-gray-500">
                  {formatFileSize(document.fileSize)} •{" "}
                  {formatDate(document.createdAt)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {document.isEncrypted && (
                <div title="Encrypted">
                  <Shield className="w-4 h-4 text-green-600" />
                </div>
              )}
              {document.isPrivate && (
                <div title="Private">
                  <Lock className="w-4 h-4 text-blue-600" />
                </div>
              )}

              <div className="relative">
                <button
                  onClick={() =>
                    setShowDropdown(
                      showDropdown === document.id ? null : document.id,
                    )
                  }
                  className="w-6 h-6 text-gray-400 hover:text-gray-600"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>

                {showDropdown === document.id && (
                  <div className="absolute right-0 top-6 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                    <button
                      onClick={() => {
                        onDocumentSelect?.(document);
                        setShowDropdown(null);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={() => {
                        handleDownload(document);
                        setShowDropdown(null);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                    <button
                      onClick={() => {
                        // TODO: Implement sharing
                        setShowDropdown(null);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <Share2 className="w-4 h-4" />
                      Share
                    </button>
                    <hr className="my-1" />
                    <button
                      onClick={() => {
                        handleDelete(document);
                        setShowDropdown(null);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Category */}
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${category?.color || "bg-gray-100 text-gray-800"}`}
            >
              {category?.icon} {category?.label || document.category}
            </span>
          </div>

          {/* Description */}
          {document.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {document.description}
            </p>
          )}

          {/* Tags */}
          {document.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {document.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </span>
              ))}
              {document.tags.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{document.tags.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => onDocumentSelect?.(document)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Open
              </button>
              <button
                onClick={() => handleDownload(document)}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Download
              </button>
            </div>

            {document.user && (
              <span className="text-xs text-gray-500">
                {document.user.firstName} {document.user.lastName}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const DocumentRow: React.FC<{ document: StoredDocument }> = ({
    document,
  }) => {
    const category =
      DOCUMENT_CATEGORIES[
        document.category as keyof typeof DOCUMENT_CATEGORIES
      ];

    return (
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => onDocumentSelect?.(document)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onDocumentSelect?.(document);
          }
        }}
        tabIndex={0}
        role="button"
        aria-label={`Open ${document.originalName}`}
      >
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-3">
            <div>{getFileIcon(document.mimeType)}</div>
            <div>
              <div className="text-sm font-medium text-gray-900">
                {document.originalName}
              </div>
              <div className="text-xs text-gray-500">
                {formatFileSize(document.fileSize)}
              </div>
            </div>
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${category?.color || "bg-gray-100 text-gray-800"}`}
          >
            {category?.icon} {category?.label || document.category}
          </span>
        </td>

        <td className="px-6 py-4">
          <div className="text-sm text-gray-900 max-w-xs truncate">
            {document.description || "-"}
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center gap-2">
            {document.isEncrypted && (
              <div title="Encrypted">
                <Shield className="w-4 h-4 text-green-600" />
              </div>
            )}
            {document.isPrivate && (
              <div title="Private">
                <Lock className="w-4 h-4 text-blue-600" />
              </div>
            )}
          </div>
        </td>

        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(document.createdAt)}
        </td>

        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDocumentSelect?.(document);
              }}
              className="text-blue-600 hover:text-blue-900"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDownload(document);
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(document);
              }}
              className="text-red-600 hover:text-red-900"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">My Documents</h2>
          <p className="text-sm text-gray-600">
            {filteredDocuments.length} of {documents.length} documents
          </p>
        </div>

        {allowViewModeToggle && (
          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded ${viewMode === "grid" ? "bg-white shadow-sm" : "text-gray-600"}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded ${viewMode === "list" ? "bg-white shadow-sm" : "text-gray-600"}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                aria-label="Search documents"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div className="sm:w-48">
            <select
              aria-label="Filter documents by category"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Categories</option>
              {Object.entries(DOCUMENT_CATEGORIES).map(([key, category]) => (
                <option key={key} value={key}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Privacy Filter */}
          <div className="flex items-center">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={showPrivateOnly}
                onChange={(e) => setShowPrivateOnly(e.target.checked)}
                className="mr-2 text-blue-600"
              />
              <Lock className="w-4 h-4 mr-1 text-blue-600" />
              <span className="text-sm">Only private</span>
            </label>
          </div>
        </div>
      </div>

      {/* Documents */}
      {filteredDocuments.length === 0 ? (
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No documents found
          </h3>
          <p className="text-gray-600 mb-4">
            {documents.length === 0
              ? "You have not uploaded any documents yet."
              : "No documents match the current filters."}
          </p>
          {showUpload && documents.length === 0 && (
            <button
              onClick={() => window.location.reload()} // TODO: Navigate to upload
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Upload first document
            </button>
          )}
        </div>
      ) : effectiveViewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDocuments.map((document) => (
            <DocumentCard key={document.id} document={document} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Security
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDocuments.map((document) => (
                <DocumentRow key={document.id} document={document} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-0"
          role="button"
          tabIndex={0}
          onClick={() => setShowDropdown(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setShowDropdown(null);
            }
          }}
          aria-label="Close dropdown"
        />
      )}
    </div>
  );
};
